/* eslint-disable max-lines-per-function */
import { sortBy } from "lodash";
import React from "react";
import { Form, Formik, FormikHelpers } from "formik";
import { useQuery } from "@apollo/client";
import { gql } from "graphql-tag";
import * as yup from "yup";
import { Grid } from "@material-ui/core";
import { Button } from "components/haven-design/buttons";
import { MultiSelect } from "components/haven-design/form";
import { SelectRole, getHighestRole } from "components/haven-design/form/special-fields";
import { Roles } from "components/auth/types";
import { ServerError, LoadingSpinner } from "components/graphql-views";
import {
  InputWithErrorLabel as Input,
  MaskedInputWithErrorLabel as MaskedInput,
} from "components/haven-design/form/inputs";
import { US_PHONE_MASK } from "utils/phone";
import { UserLoginPreflight, UserLoginPreflightVariables, getListsByOrg } from "types/gql-types";
import { apolloClient } from "data/graphql";
import { SIGN_IN_ATTEMPT } from "components/login/query";
import { ListOption } from "pages/dashboard/settings/pages/team/shared-create-update";


const gqlToOption = ({ id, name }: { id: string; name: string }) => ({ id, label: name });

const LISTS = gql`
  query getListsByOrg($orgId: ID!) {
    organization(id: $orgId) {
      id
      interestLists {
        id
        name
      }
    }
  }
`;

type InputData = {
  email: string;
  firstName: string;
  lastName: string;
  lists: ListOption[];
  title: string | null;
  directPhone: string | null;
  organizationId: string | null;
}
interface Props {
  onSubmit: (values: InputData & { roles: Roles | "" }, actions: FormikHelpers<any>) => void;
  isSending?: boolean;
  initialData: InputData & {roles: Roles[]};
}

const validateIdentifierAvailable =
  async (
    identifier: string | undefined,
    originalValue: string | undefined,
  ) => {
    // This helps prevent server hits when the email isn't valid.
    yup.string().email().validateSync(identifier);

    if (
      identifier &&
      identifier !== originalValue
    ) {
      const usernamePreflight =
        apolloClient.mutate<UserLoginPreflight, UserLoginPreflightVariables>({
          mutation: SIGN_IN_ATTEMPT,
          variables: { username: identifier },
        });

      const result = await usernamePreflight;
      const { userLoginPreflight } = result.data || { userLoginPreflight: null };
      return userLoginPreflight?.login === null;
    }
    return true;
  };

const UserForm: React.FC<Props> = (props) => {
  const formattedData = {
    ...props.initialData,
    roles: getHighestRole(props.initialData.roles),
  };
  if (!formattedData.organizationId) return <ServerError desc="User Missing Organization data" />;
  const variables = {
    orgId: formattedData.organizationId,
  };

  const schema = yup.object().shape({
    organizationId: yup.string().required(),
    firstName: yup.string().required(),
    lastName: yup.string().required(),
    title: yup.string().nullable(),
    directPhone: yup.string().validPhoneNumber("Invalid phone number").nullable(),
    email: yup.string().required().email()
      .test(
        "emailAvailable",
        "Account already exists with this email",
        // eslint-disable-next-line no-return-await
        async (val) => await validateIdentifierAvailable(val, props.initialData.email),
      ),
    roles: yup.string().required().oneOf(["propManager", "supervisor", "client"]),
    lists: yup.array().required(),
  });

  const { loading, error, data } = useQuery<getListsByOrg>(LISTS, { fetchPolicy: "cache-and-network", variables });
  if (error) return <ServerError desc={error.message} />;
  if (loading) return <LoadingSpinner />;
  if (!data) return <ServerError desc="Missing Data" />;

  const lists = data.organization.interestLists;
  const propOpts = sortBy(lists.map(gqlToOption), "label");

  return (
    <div style={{ maxWidth: 800 }}>
      <Formik
        initialValues={formattedData}
        onSubmit={(values, actions) => props.onSubmit(values, actions)}
        validationSchema={schema}>
        {({ isSubmitting }) => (
          <Form>
            <Grid container spacing={2}>
              <Grid item md={6} xs={8}>
                <Input required margin="none" label="First Name" name="firstName" type="text" />
              </Grid>
              <Grid item md={6} xs={8}>
                <Input required margin="none" label="Last Name" name="lastName" type="text" />
              </Grid>

              <Grid item md={6} xs={8}>
                <Input required margin="none" label="Email Address" name="email" type="text" />
              </Grid>
              <Grid item md={6} xs={8}>
                <SelectRole required name="roles" label="Account Role" />
              </Grid>

              <Grid item md={6} xs={8}>
                <MaskedInput
                  margin="none"
                  label="Phone"
                  name="directPhone"
                  mask={US_PHONE_MASK}
                  guide={false}
                />
              </Grid>
              <Grid item md={6} xs={8}>
                <Input margin="none" name="title" label="Title" type="text" autoComplete="off" />
              </Grid>

              <Grid item md={12}>
                <MultiSelect
                  label="Properties"
                  name="lists"
                  options={propOpts}
                  selectedOptions={props.initialData.lists}
                  height={300}
                />
              </Grid>

              <Grid item md={12}>
                <Button
                  size="L"
                  color="secondary"
                  onClick={() => history.back()}
                  style={{ marginRight: 15 }}
                >
                  Back
                </Button>

                <Button
                  size="L"
                  color="primary"
                  type="submit"
                  disabled={isSubmitting && props.isSending}
                >
                  {isSubmitting && props.isSending ? "Sending" : "Submit"}
                </Button>
              </Grid>

            </Grid>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default UserForm;
